import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { NoData } from "@/components/common/section-components/NoData";
import { ProductPlanificationFilter } from "@/components/common/section-components/filters/ProductPlanificationFilter";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { AppRoutes } from "@/constants/routes";
import { Flex, Text } from "@chakra-ui/react";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";

import {
  clearFiltersHandler,
  fetchProductPlanificationListHandler,
  getDefaultProductPlanificationFilters,
  submitFiltersHandler,
} from "./schema/handler-functions";
import {
  ProductPlanificationListItem,
  ProductPlanificationListResponse,
  productPlanificationDefaultFilters,
} from "./schema/product-planification.schema";
import { productPlanificationHeaderElements } from "./schema/header-elements";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { ProductPlanificationStatus } from "@/constants/entity-status";
import { ProductPlanificationActionElements } from "@/components/common/section-components/action/ProductPlanificationActionElements";
import { TriangleAlert } from "lucide-react";
import { AppColors } from "@/styles/colors";

export const ProductPlanificationPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const { control, handleSubmit, setValue } = useForm<FieldValues>({
    defaultValues: productPlanificationDefaultFilters as any,
  });

  const values = useWatch({ control });
  const filtersEmpty = useMemo(
    () => Object.values(values ?? {}).every((v) => isEmpty(v)),
    [values],
  );

  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<any>();
  const [page, setPage] = useState<number>(1);

  const [list, setList] = useState<ProductPlanificationListResponse | null>(
    null,
  );

  const getDesvioLabel = (val: boolean | undefined) => {
    if (typeof val !== "boolean") return "-";
    return (
      <Flex width="100%" justify="center">
        {val ? (
          <TriangleAlert color={AppColors.WARNING_ICON} />
        ) : (
          <Text fontWeight={500}>-</Text>
        )}
      </Flex>
    );
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  const handleAdd = () => {
    navigate(AppRoutes.PRODUCT_PLANIFICATION_CREATION);
  };

  const fetchList = async (filters?: any, showLoad = true) => {
    await fetchProductPlanificationListHandler({
      filters,
      showLoad,
      setLoading,
      setList,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (isActive) {
      const initialFilters = getDefaultProductPlanificationFilters();
      setCurrentFilters(initialFilters);
      setFiltersApplied(true);
      fetchList(initialFilters);
    }
  }, [isActive]);

  const submitFilters = async (event: FieldValues) => {
    await submitFiltersHandler({
      event,
      setPage,
      fetchList,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      filtersEmpty,
    });
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      setValue,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      setCurrentFilters,
      fetchList,
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchList({ ...currentFilters, "P.Pagina": nextPage });
  };

  const handleDetail = (item: ProductPlanificationListItem) => {
    navigate(
      AppRoutes.PRODUCT_PLANIFICATION_DETAIL.replace(":id", String(item.id)),
    );
  };

  const getActionElements = (item: any) => (
    <ProductPlanificationActionElements
      handleDetail={(id) => handleDetail({ id } as any)}
      item={item}
    />
  );

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      data-testid="product-planification-page"
      gap="12px"
    >
      <SectionHeader
        title={"Planificación productiva"}
        addButtonLabel="Agregar"
        handleAdd={handleAdd}
        loading={loading}
        showButtons={true}
      />{" "}
      <ProductPlanificationFilter
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        filtersApplied={filtersApplied}
        disableFilters={loading}
        loadingFilters={loading}
        disableApply={filtersEmpty && !filtersApplied}
      />
      <Flex flexDir="column">
        {Boolean(!loading && !list?.data?.length) && <NoData />}

        {Boolean(!loading && list?.data?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                list?.data?.map((el: ProductPlanificationListItem) => ({
                  ...el,
                  displayFechaCreacion: el?.fechaCreacion
                    ? dayjs(el?.fechaCreacion).format("DD/MM/YYYY HH:mm")
                    : "-",
                  displayTitularCuenta:
                    el?.titularCuenta && el?.cuentaOrigen
                      ? `${el.titularCuenta} - ${el.cuentaOrigen}`
                      : (el?.titularCuenta ?? "-"),
                  displayDesvio: getDesvioLabel(el?.desvioHectareas),
                  estado: (
                    <RequestStatusBadge
                      status={el?.estado as ProductPlanificationStatus}
                    />
                  ),
                })) ?? []
              }
              headers={productPlanificationHeaderElements}
              actionElements={getActionElements}
              
            />

            {Number(list?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={list?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
